/* CRM Lead Detail — smart lead card with behavioural context.
 *
 * Layout:
 *   Header: name, contact chips, status/priority controls, action buttons
 *   Left column (2/3): Timeline (auto + manual activities)
 *   Right column (1/3): SmartContext, Notes, Tasks, Related property
 */
import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Phone, Mail, MessageCircle, Loader2, ChevronDown,
  User as UserIcon, Home as HomeIcon, Sparkles, Eye, Repeat, MousePointerClick,
  Send, Plus, CheckCircle2, Circle, Calendar, StickyNote, Activity,
} from 'lucide-react';
import { api, friendlyError, imgUrl } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';

const STATUS_META = {
  nuevo:       { label: 'Nuevo',       color: 'bg-blue-500',    hex: '#3b82f6' },
  contactado:  { label: 'Contactado',  color: 'bg-indigo-500',  hex: '#6366f1' },
  visita:      { label: 'Visita',      color: 'bg-amber-500',   hex: '#f59e0b' },
  negociacion: { label: 'Negociación', color: 'bg-orange-500',  hex: '#f97316' },
  cerrado:     { label: 'Cerrado',     color: 'bg-emerald-500', hex: '#10b981' },
  perdido:     { label: 'Perdido',     color: 'bg-rose-500',    hex: '#f43f5e' },
};
const STATUSES = ['nuevo', 'contactado', 'visita', 'negociacion', 'cerrado', 'perdido'];
const PRIORITIES = [{ v: 'alta', l: '🔴 Alta' }, { v: 'media', l: '🟡 Media' }, { v: 'baja', l: '🟢 Baja' }];

const ACTIVITY_ICON = {
  lead_created: UserIcon, visit_scheduled: Calendar, status_changed: Activity,
  message_received: MessageCircle, note_added: StickyNote, task_created: Circle,
  task_completed: CheckCircle2, reassigned: UserIcon,
};

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('es-CO', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}
function relTime(iso) {
  if (!iso) return '';
  try {
    const diff = Date.now() - new Date(iso).getTime();
    if (diff < 60_000) return 'ahora';
    if (diff < 3_600_000) return `hace ${Math.floor(diff / 60_000)} min`;
    if (diff < 86_400_000) return `hace ${Math.floor(diff / 3_600_000)} h`;
    return `hace ${Math.floor(diff / 86_400_000)} d`;
  } catch { return ''; }
}

// ─────────────────────────────────────── Smart Context card

function SmartContext({ leadId }) {
  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.crmSmartContext(leadId).then(setCtx).catch(() => setCtx(null)).finally(() => setLoading(false));
  }, [leadId]);
  if (loading) return <div className="bg-white rounded-2xl border border-gray-100 p-4"><Loader2 className="w-4 h-4 animate-spin text-gray-400" /></div>;
  if (!ctx) return null;
  return (
    <div className="bg-gradient-to-br from-habita/5 to-white border border-habita/20 rounded-2xl p-4" data-testid="crm-smart-context">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-4 h-4 text-habita" />
        <p className="text-sm font-semibold text-habita-dark">Ficha inteligente</p>
      </div>
      {ctx.note ? (
        <div title="Este lead fue creado desde el histórico (inspecciones/mensajes previos) y no tiene sesión analítica asociada. Los nuevos leads a partir de ahora sí registrarán su sesión automáticamente." data-testid="ctx-empty-tooltip">
          <p className="text-xs text-gray-500 italic">{ctx.note}</p>
          <p className="text-[10px] text-gray-400 mt-1.5">Pasa el cursor para saber por qué.</p>
        </div>
      ) : (
        <div className="space-y-2 text-sm">
          <Stat icon={Eye} label="Propiedades vistas antes" value={ctx.properties_viewed} data-testid="ctx-views" />
          <Stat icon={HomeIcon} label="Únicas" value={ctx.unique_properties_viewed} data-testid="ctx-unique" />
          <Stat icon={Repeat} label="Sesiones" value={ctx.sessions} data-testid="ctx-sessions" />
          <Stat icon={MousePointerClick} label="Clic en WhatsApp previo" value={ctx.whatsapp_click_before_lead ? 'Sí' : 'No'} data-testid="ctx-wa" />
          {ctx.traffic_source && <Stat icon={Activity} label="Fuente de tráfico" value={ctx.traffic_source} data-testid="ctx-traffic" />}
          {ctx.first_seen_at && <p className="text-[11px] text-gray-500 mt-1">Primera visita: {fmtDate(ctx.first_seen_at)}</p>}
          {ctx.last_property_viewed && (
            <div className="mt-3 pt-3 border-t border-habita/10">
              <p className="text-[11px] uppercase tracking-widest text-gray-500 mb-1">Última propiedad vista</p>
              <Link to={`/propiedad/${ctx.last_property_viewed.id}`} className="flex items-center gap-2 text-sm text-habita-dark font-medium hover:underline">
                {ctx.last_property_viewed.images?.[0] && <img src={imgUrl(ctx.last_property_viewed.images[0])} alt="" className="w-8 h-8 rounded object-cover" />}
                <span className="truncate">{ctx.last_property_viewed.title}</span>
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-2 text-gray-600 text-xs">
        <Icon className="w-3.5 h-3.5" />
        <span>{label}</span>
      </div>
      <span className="font-semibold text-gray-900 tabular-nums text-sm">{value}</span>
    </div>
  );
}

// ─────────────────────────────────────── Notes + Tasks

function NotesCard({ lead, onAdded }) {
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();
  const submit = async () => {
    if (!text.trim()) return;
    setSaving(true);
    try {
      await api.crmAddNote(lead.id, text.trim());
      setText('');
      onAdded();
    } catch (err) { toast({ title: 'No pudimos guardar la nota', description: friendlyError(err), variant: 'destructive' }); }
    finally { setSaving(false); }
  };
  return (
    <div className="bg-white border border-gray-100 rounded-2xl p-4" data-testid="crm-notes">
      <p className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2"><StickyNote className="w-4 h-4" /> Notas privadas</p>
      <div className="space-y-2 mb-3 max-h-[240px] overflow-y-auto">
        {(lead.notes || []).map(n => (
          <div key={n.id} className="text-xs bg-yellow-50 border border-yellow-100 rounded-lg p-2.5">
            <p className="text-gray-800 whitespace-pre-wrap">{n.text}</p>
            <p className="text-[10px] text-gray-500 mt-1">{n.author_name} · {relTime(n.created_at)}</p>
          </div>
        ))}
        {(lead.notes || []).length === 0 && <p className="text-xs text-gray-400 italic text-center py-3">Sin notas aún.</p>}
      </div>
      <textarea
        value={text}
        onChange={e => setText(e.target.value)}
        rows={2}
        placeholder="Agrega una nota interna..."
        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:border-habita outline-none resize-none"
        data-testid="crm-note-input"
      />
      <button
        onClick={submit}
        disabled={saving || !text.trim()}
        className="mt-2 w-full h-9 rounded-full bg-habita text-white text-sm font-semibold hover:bg-habita-dark disabled:opacity-60 flex items-center justify-center gap-1.5"
        data-testid="crm-note-submit"
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Guardar nota
      </button>
    </div>
  );
}

function TasksCard({ lead, onChanged }) {
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();
  const add = async () => {
    if (!text.trim()) return;
    setSaving(true);
    try {
      await api.crmAddTask(lead.id, { title: text.trim() });
      setText('');
      onChanged();
    } catch (err) { toast({ title: 'No pudimos crear la tarea', description: friendlyError(err), variant: 'destructive' }); }
    finally { setSaving(false); }
  };
  const toggle = async (taskId, complete) => {
    try {
      await api.crmCompleteTask(lead.id, taskId, complete);
      onChanged();
    } catch (err) { toast({ title: 'No pudimos actualizar', description: friendlyError(err), variant: 'destructive' }); }
  };
  return (
    <div className="bg-white border border-gray-100 rounded-2xl p-4" data-testid="crm-tasks">
      <p className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> Tareas y recordatorios</p>
      <ul className="space-y-1.5 mb-3 max-h-[200px] overflow-y-auto">
        {(lead.tasks || []).map(t => (
          <li key={t.id} className="flex items-start gap-2 text-sm">
            <button onClick={() => toggle(t.id, !t.completed_at)} className="mt-0.5 text-gray-400 hover:text-habita" data-testid={`task-toggle-${t.id}`}>
              {t.completed_at ? <CheckCircle2 className="w-4 h-4 text-habita" /> : <Circle className="w-4 h-4" />}
            </button>
            <span className={`flex-1 ${t.completed_at ? 'text-gray-400 line-through' : 'text-gray-800'}`}>{t.title}</span>
          </li>
        ))}
        {(lead.tasks || []).length === 0 && <li className="text-xs text-gray-400 italic text-center py-3">Sin tareas.</li>}
      </ul>
      <div className="flex gap-2">
        <input
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') add(); }}
          placeholder="Nueva tarea (ej: Llamar mañana)"
          className="flex-1 h-9 px-3 text-sm border border-gray-200 rounded-full focus:border-habita outline-none"
          data-testid="crm-task-input"
        />
        <button onClick={add} disabled={saving || !text.trim()} className="w-9 h-9 rounded-full bg-habita text-white hover:bg-habita-dark disabled:opacity-60 flex items-center justify-center" data-testid="crm-task-submit">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────── Timeline

function Timeline({ activities }) {
  return (
    <div className="bg-white border border-gray-100 rounded-2xl p-5" data-testid="crm-timeline">
      <p className="text-sm font-semibold text-gray-800 mb-4 flex items-center gap-2"><Activity className="w-4 h-4" /> Timeline</p>
      {activities.length === 0 ? (
        <p className="text-xs text-gray-400 italic py-6 text-center">Sin actividad registrada.</p>
      ) : (
        <ul className="space-y-4">
          {activities.map((ev, i) => {
            const Icon = ACTIVITY_ICON[ev.kind] || Activity;
            return (
              <li key={ev.id || i} className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-habita/10 text-habita flex items-center justify-center shrink-0">
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-800">{ev.text}</p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {ev.actor_name && <span>{ev.actor_name} · </span>}
                    {fmtDate(ev.created_at)}
                  </p>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// ─────────────────────────────────────── Main

export default function CRMLeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingStatus, setSavingStatus] = useState(false);

  const load = async () => {
    try {
      const d = await api.crmGetLead(id);
      setLead(d);
    } catch (err) {
      toast({ title: 'No pudimos cargar el lead', description: friendlyError(err), variant: 'destructive' });
      if (err?.status === 404 || err?.status === 403) navigate('/crm');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [id]);

  const patch = async (updates) => {
    setSavingStatus(true);
    try {
      await api.crmUpdateLead(id, updates);
      await load();
    } catch (err) { toast({ title: 'No pudimos guardar', description: friendlyError(err), variant: 'destructive' }); }
    finally { setSavingStatus(false); }
  };

  if (loading) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-habita" /></div>;
  }
  if (!lead) return null;

  const waHref = lead.phone ? `https://wa.me/${lead.phone.replace(/[^0-9]/g, '')}?text=Hola%20${encodeURIComponent(lead.name)}` : null;
  const telHref = lead.phone ? `tel:${lead.phone}` : null;
  const mailHref = lead.email ? `mailto:${lead.email}` : null;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="crm-lead-detail">
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-[1400px] mx-auto px-6 py-6">
          <Link to="/crm" className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-habita mb-4">
            <ArrowLeft className="w-4 h-4" /> Volver a la bandeja
          </Link>
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-habita/10 text-habita flex items-center justify-center text-xl font-bold">
                {(lead.name || '?').slice(0, 1).toUpperCase()}
              </div>
              <div>
                <h1 className="font-serif text-3xl font-bold text-habita-dark">{lead.name}</h1>
                <div className="flex items-center gap-3 mt-1 text-sm text-gray-600 flex-wrap">
                  {lead.email && <span className="flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> {lead.email}</span>}
                  {lead.phone && <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> {lead.phone}</span>}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {waHref && <a href={waHref} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 px-4 h-10 rounded-full bg-emerald-500 text-white text-sm font-semibold hover:bg-emerald-600" data-testid="crm-action-whatsapp"><MessageCircle className="w-4 h-4" /> WhatsApp</a>}
              {telHref && <a href={telHref} className="flex items-center gap-1.5 px-4 h-10 rounded-full bg-white border border-gray-200 hover:border-habita text-gray-800 text-sm font-semibold" data-testid="crm-action-call"><Phone className="w-4 h-4" /> Llamar</a>}
              {mailHref && <a href={mailHref} className="flex items-center gap-1.5 px-4 h-10 rounded-full bg-white border border-gray-200 hover:border-habita text-gray-800 text-sm font-semibold" data-testid="crm-action-email"><Mail className="w-4 h-4" /> Email</a>}
            </div>
          </div>

          {/* Status / priority controls */}
          <div className="mt-5 flex items-center gap-3 flex-wrap">
            <div className="relative">
              <select
                value={lead.status}
                onChange={e => patch({ status: e.target.value })}
                disabled={savingStatus}
                className="appearance-none h-9 pl-7 pr-8 rounded-full text-xs font-semibold text-white border-none outline-none cursor-pointer"
                style={{ backgroundColor: STATUS_META[lead.status]?.hex || '#3b82f6' }}
                data-testid="crm-status-select"
              >
                {STATUSES.map(s => <option key={s} value={s} className="bg-white text-gray-800">{STATUS_META[s].label}</option>)}
              </select>
              <span className="absolute left-2 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-white/80" />
              <ChevronDown className="w-3.5 h-3.5 text-white/80 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>
            <select
              value={lead.priority}
              onChange={e => patch({ priority: e.target.value })}
              disabled={savingStatus}
              className="h-9 px-3 rounded-full text-xs font-semibold bg-white border border-gray-200 outline-none focus:border-habita"
              data-testid="crm-priority-select"
            >
              {PRIORITIES.map(p => <option key={p.v} value={p.v}>{p.l}</option>)}
            </select>
            <span className="text-xs text-gray-500 capitalize">Fuente: <strong className="text-gray-800">{lead.source}</strong></span>
            {(lead.tags || []).map((tag) => (
              <span key={tag} className="text-[11px] px-2 py-0.5 rounded-full bg-habita/10 text-habita capitalize">{tag.replace('_', ' ')}</span>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {lead.property && (
              <div className="bg-white border border-gray-100 rounded-2xl p-4" data-testid="crm-property-card">
                <p className="text-xs uppercase tracking-widest text-gray-500 mb-2">Propiedad consultada</p>
                <Link to={`/propiedad/${lead.property.id}`} className="flex items-center gap-3 hover:text-habita">
                  {lead.property.images?.[0] && <img src={imgUrl(lead.property.images[0])} alt="" className="w-16 h-16 rounded-xl object-cover" />}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 truncate">{lead.property.title}</p>
                    <p className="text-xs text-gray-500 truncate">{lead.property.city} · {lead.property.address}</p>
                    {lead.property.price && <p className="text-sm text-habita font-semibold mt-1">${lead.property.price.toLocaleString('es-CO')}</p>}
                  </div>
                </Link>
              </div>
            )}
            <Timeline activities={lead.activities || []} />
          </div>
          <div className="space-y-4">
            <SmartContext leadId={lead.id} />
            <NotesCard lead={lead} onAdded={load} />
            <TasksCard lead={lead} onChanged={load} />

            <div className="bg-white border border-gray-100 rounded-2xl p-4 text-xs text-gray-600 space-y-1.5">
              <p><span className="font-semibold text-gray-800">Creado:</span> {fmtDate(lead.created_at)}</p>
              {lead.first_contact_at && <p><span className="font-semibold text-gray-800">Primer contacto:</span> {fmtDate(lead.first_contact_at)}</p>}
              {lead.response_time_minutes != null && <p><span className="font-semibold text-gray-800">Tiempo de respuesta:</span> {lead.response_time_minutes} min</p>}
              {lead.first_visit_at && <p><span className="font-semibold text-gray-800">Primera visita:</span> {fmtDate(lead.first_visit_at)}</p>}
              {lead.assigned_agent && (
                <p><span className="font-semibold text-gray-800">Asignado a:</span> {lead.assigned_agent.name}</p>
              )}
              {lead.agency && (
                <p><span className="font-semibold text-gray-800">Inmobiliaria:</span> {lead.agency.name}</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
